import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_FORM_FIELD_ATTRIBUTE } from "./constants";
import FormValidationFieldShell, {
  type FormValidationFieldShellProps,
} from "./FormValidationFieldShell";
import SlidePanelValidationEffects from "./SlidePanelValidationEffects";
import SlidePanelValidationErrorNavigation from "./SlidePanelValidationErrorNavigation";
import SlidePanelValidationSummary from "./SlidePanelValidationSummary";
import type {
  FormApi,
  FormSlidePanelSummaryContent,
  FormValidationFailureHandler,
} from "./types";
import {
  buildValidationErrorItems,
  type ValidationErrorItem,
  type ValidationFieldMeta,
} from "./validationSummaryUtils";

export interface FormValidationSummaryDataTestIds {
  summary?: string;
  goToFirst?: string;
  summaryLink?: string;
  errorNavigation?: string;
  previousError?: string;
  nextError?: string;
  fieldPrefix?: string;
}

export interface UseFormValidationSummaryOptions<
  TValues extends Record<string, unknown>,
> {
  resolveErrorItems: (formApi: FormApi<TValues>) => ValidationErrorItem[];
  fieldAttribute?: string;
  dataTestIdPrefix?: string;
  dataTestIds?: FormValidationSummaryDataTestIds;
  i18nPrefix?: string;
}

export interface UseFormValidationSummaryResult<
  TValues extends Record<string, unknown>,
> {
  validationAttempted: boolean;
  onValidationFailure: FormValidationFailureHandler<TValues>;
  renderSummaryContent: FormSlidePanelSummaryContent<TValues>;
  ValidationEffects: React.FC<{ formApi: FormApi<TValues> }>;
  FieldShell: React.FC<
    Omit<FormValidationFieldShellProps, "fieldAttribute" | "dataTestIdPrefix">
  >;
  navigateToError: (fieldName: string, index: number) => void;
  getErrorItems: (formApi: FormApi<TValues>) => ValidationErrorItem[];
  isFieldInvalid: (fieldName: string, formApi: FormApi<TValues>) => boolean;
}

export function useFormValidationSummary<
  TValues extends Record<string, unknown>,
>({
  resolveErrorItems,
  fieldAttribute = DEFAULT_FORM_FIELD_ATTRIBUTE,
  dataTestIdPrefix = "slide-panel-validation",
  dataTestIds,
  i18nPrefix = "slidePanel.validation",
}: UseFormValidationSummaryOptions<TValues>): UseFormValidationSummaryResult<TValues> {
  const { t } = useTranslation();
  const testIds = useMemo(
    () => ({
      summary: dataTestIds?.summary ?? `${dataTestIdPrefix}-summary`,
      goToFirst:
        dataTestIds?.goToFirst ?? `${dataTestIdPrefix}-go-to-first-error`,
      summaryLink:
        dataTestIds?.summaryLink ?? `${dataTestIdPrefix}-summary-link`,
      errorNavigation:
        dataTestIds?.errorNavigation ?? `${dataTestIdPrefix}-error-navigation`,
      previousError:
        dataTestIds?.previousError ?? `${dataTestIdPrefix}-previous-error`,
      nextError: dataTestIds?.nextError ?? `${dataTestIdPrefix}-next-error`,
      fieldPrefix: dataTestIds?.fieldPrefix ?? `${dataTestIdPrefix}-field`,
    }),
    [dataTestIdPrefix, dataTestIds],
  );
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState<
    string | undefined
  >();
  const [activeErrorIndex, setActiveErrorIndex] = useState(0);

  const onValidationFailure = useCallback<
    FormValidationFailureHandler<TValues>
  >(errorPaths => {
    setValidationAttempted(true);
    setActiveErrorIndex(0);
    setPendingFocusField(errorPaths[0] || "first-error");
  }, []);

  const navigateToError = useCallback((fieldName: string, index: number) => {
    setActiveErrorIndex(index);
    setPendingFocusField(fieldName);
  }, []);

  const clearPendingFocusField = useCallback(() => {
    setPendingFocusField(undefined);
  }, []);

  const getErrorItems = useCallback(
    (formApi: FormApi<TValues>) =>
      validationAttempted ? resolveErrorItems(formApi) : [],
    [resolveErrorItems, validationAttempted],
  );

  const isFieldInvalid = useCallback(
    (fieldName: string, formApi: FormApi<TValues>) =>
      getErrorItems(formApi).some(item => item.name === fieldName),
    [getErrorItems],
  );

  const renderSummaryContent: FormSlidePanelSummaryContent<TValues> =
    useCallback(
      (formApi: FormApi<TValues>) => {
        const validationErrorItems = getErrorItems(formApi);
        const firstErrorItem = validationErrorItems[0];
        if (!firstErrorItem) return null;

        return (
          <>
            <SlidePanelValidationSummary
              title={t(`${i18nPrefix}.requiredSummary`, {
                count: validationErrorItems.length,
              })}
              description={t(`${i18nPrefix}.requiredDescription`)}
              items={validationErrorItems.map((item, index) => ({
                id: item.name,
                label: item.label,
                ariaLabel: `${item.label}: ${item.message}`,
                onClick: () => navigateToError(item.name, index),
              }))}
              goToFirstLabel={t(`${i18nPrefix}.goToFirstError`)}
              onGoToFirst={() => navigateToError(firstErrorItem.name, 0)}
              dataTestId={testIds.summary}
              goToFirstDataTestId={testIds.goToFirst}
              itemDataTestId={testIds.summaryLink}
            />
            <SlidePanelValidationErrorNavigation
              activeErrorIndex={activeErrorIndex}
              errorItems={validationErrorItems}
              onNavigate={navigateToError}
              i18nPrefix={i18nPrefix}
              dataTestId={testIds.errorNavigation}
              previousErrorDataTestId={testIds.previousError}
              nextErrorDataTestId={testIds.nextError}
            />
          </>
        );
      },
      [
        activeErrorIndex,
        getErrorItems,
        i18nPrefix,
        navigateToError,
        t,
        testIds,
      ],
    );

  const ValidationEffects = useMemo(
    () =>
      function ValidationEffectsComponent({
        formApi,
      }: {
        formApi: FormApi<TValues>;
      }) {
        const errorItems = getErrorItems(formApi);

        return (
          <SlidePanelValidationEffects
            activeErrorIndex={activeErrorIndex}
            errorItems={errorItems}
            pendingFocusField={pendingFocusField}
            onActiveErrorIndexChange={setActiveErrorIndex}
            onPendingFocusHandled={clearPendingFocusField}
            fieldAttribute={fieldAttribute}
          />
        );
      },
    [
      activeErrorIndex,
      clearPendingFocusField,
      fieldAttribute,
      getErrorItems,
      pendingFocusField,
    ],
  );

  const FieldShell = useMemo(
    () =>
      function FieldShellComponent(
        props: Omit<
          FormValidationFieldShellProps,
          "fieldAttribute" | "dataTestIdPrefix"
        >,
      ) {
        return (
          <FormValidationFieldShell
            {...props}
            fieldAttribute={fieldAttribute}
            dataTestIdPrefix={testIds.fieldPrefix}
          />
        );
      },
    [fieldAttribute, testIds.fieldPrefix],
  );

  return {
    validationAttempted,
    onValidationFailure,
    renderSummaryContent,
    ValidationEffects,
    FieldShell,
    navigateToError,
    getErrorItems,
    isFieldInvalid,
  };
}

export type { ValidationErrorItem, ValidationFieldMeta };
export { buildValidationErrorItems };
